import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConexionServiceProvider, ConnectionStatus } from '../conexion-service/conexion-service';
import { AppPlatformService } from '../platform/app-platform.service';

const FORCED_OFFLINE_KEY = 'chooseOffline';

/**
 * El único criterio de «estoy sin conexión»: el modo de la app. La app está en modo sin conexión cuando
 * el teléfono perdió la red o cuando el alumno activó «Modo sin conexión», aunque el teléfono tenga red.
 *
 * Con la app en modo sin conexión todo se guarda en local y se encola; la red real del dispositivo ya no
 * se consulta para decidirlo. Solo se mira para saber si el alumno PUEDE cambiar de modo (no tiene
 * sentido «reconectar» sin red).
 *
 * El modo sin conexión solo existe en la app nativa: en web no hay base local ni descargas, y
 * `ConexionServiceProvider` solo actualiza su estado en la app (arranca en `Offline`), así que en web
 * nunca se está sin conexión.
 */
@Injectable({ providedIn: 'root' })
export class OfflineModeService {
  /** Emite en cada cambio de modo, y el valor actual al suscribirse. */
  readonly isOffline$: Observable<boolean> = this.connection
    .onNetworkChange()
    .pipe(map((status) => this.appPlatform.isAppPlatform() && status === ConnectionStatus.Offline));

  constructor(
    private connection: ConexionServiceProvider,
    private appPlatform: AppPlatformService
  ) {}

  isOffline(): boolean {
    return this.appPlatform.isAppPlatform() && this.connection.getCurrentNetworkStatus() === ConnectionStatus.Offline;
  }

  /** ¿El alumno activó «Modo sin conexión» a mano? Con eso, recuperar la red no lo apaga solo. */
  isManualOffline(): boolean {
    try {
      return localStorage.getItem(FORCED_OFFLINE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** ¿El teléfono tiene red? Solo sirve para saber si se puede cambiar de modo. */
  async hasDeviceConnection(): Promise<boolean> {
    return (await this.connection.getDeviceNetworkType()) !== 'none';
  }

  /**
   * Activa o desactiva «Modo sin conexión» a mano. Lo usan los dos interruptores (menú lateral y
   * header), así se comportan igual. Devuelve `false` si el teléfono no tiene red y no se pudo cambiar.
   */
  async setManualOffline(offline: boolean): Promise<boolean> {
    if (!(await this.hasDeviceConnection())) {
      this.clearManualOffline();
      return false;
    }
    try {
      if (offline) {
        localStorage.setItem(FORCED_OFFLINE_KEY, 'true');
      } else {
        localStorage.removeItem(FORCED_OFFLINE_KEY);
      }
    } catch {
      // sin localStorage el modo igual cambia; solo no sobrevive a un reinicio
    }
    await this.connection.updateNetworkStatus(offline ? ConnectionStatus.Offline : ConnectionStatus.Online);
    return true;
  }

  /**
   * El botón «Reconectar» de un contenido que pide red: pasa a modo en línea si el teléfono tiene red.
   * `fromTopic` evita la redirección que haría el cambio de modo, para quedarse en el contenido.
   */
  async goOnline(fromTopic = false): Promise<boolean> {
    if (!(await this.hasDeviceConnection())) return false;
    await this.connection.updateNetworkStatus(ConnectionStatus.Online, fromTopic);
    return true;
  }

  private clearManualOffline(): void {
    try {
      localStorage.removeItem(FORCED_OFFLINE_KEY);
    } catch {
      // nada que limpiar
    }
  }
}
